import { solscanAccountUrl } from "./components/explorerLinks";

/**
 * Evidence for the Security tab. A dated snapshot, not a data feed.
 *
 * Token controls were read from Solana mainnet with `getAccountInfo`
 * (jsonParsed, finalized) on 2026-09-15 at slot 447,260,697, and match the
 * Solscan metadata view of the mint on the same day. Re-read the mint before
 * presenting any value here as current, and move `SNAPSHOT` with the read.
 *
 * What the reads establish, and what they do not:
 *
 *   - Every token control is a PDA of the Chancery program, e.g. the mint
 *     authority is `["mint-authority"]` and the close authority
 *     `["close-mint-authority"]`. So only Chancery can exercise them.
 *   - Chancery's `["chancery-config"]` account stores the Governance,
 *     Operations, Emergency, Enforcement and Insurance Admin vaults (bytes 24
 *     to 184, in that field order), and the Program Upgrade vault is the
 *     upgrade authority on Chancery's ProgramData. Every vault derives from its
 *     Squads v4 multisig at index 0, and every multisig has a null
 *     `config_authority`.
 *   - What each role DOES is taken from the authorities disclosure, not from
 *     chain: the freeze and pause instructions gate on permission records, so a
 *     role-to-power mapping is not readable from the accounts alone.
 *   - The displayed voting thresholds and Squads timelocks were decoded from
 *     finalized mainnet multisig accounts at slot 447,362,593 on 2026-09-15.
 *     Voting members count the Squads Vote permission, not every member key.
 *     Six voting ratios differ from the retained 2026-09-04 disclosure values.
 *     Program risk-class minimums come from that disclosure, not this RPC read.
 *     Both sources are dated snapshots; neither is a live data feed.
 *
 * Also absent: governance or upgrade policy beyond the bindings above,
 * monitoring, audit findings, and an audit count.
 */

export const SNAPSHOT = {
    isoDate: "2026-09-15",
    label: "September 15, 2026",
    slot: 447_260_697,
    network: "Solana mainnet"
} as const;

/**
 * Not `WELL_KNOWN_TOKENS_STRINGS.USDv`. That constant still resolves to
 * Ex5DaKYMCN6QWFA4n67TmMwsH8MJV68RX6YXTmVM532C, which is not the live mint (it
 * is the legacy Vault program's `["mint"]` PDA). Fix it upstream rather than
 * shadowing it further.
 */
export const USDV_MINT = "USDvUSpnhCr9yBgj3UyVrD239HRUv4RsHwH2FxsWuMk";

/** The Chancery program, whose PDAs hold every USDv token control. */
export const CHANCERY_PROGRAM_ID = "ChnryP5RcZtMvP8vvVyPGUhwCg6uDJc53vCe3sxr11Sz";

/** Where "Report a Vulnerability" in the Security tab's help strip sends mail. */
export const VULNERABILITY_REPORT_EMAIL = "security@solomonlabs.org";

/** The transparency and risk disclosure, linked from the help strip. */
export const RISK_DISCLOSURE_URL = "https://docs.solomonlabs.org/usdv/transparency-and-risk";

/** Public source for the published role and timelock configuration. */
export const AUTHORITIES_DISCLOSURE_URL =
    "https://github.com/SolomonLabs/chancery-integrity-records/blob/main/docs/AUTHORITIES.md";

/** Design-system manifest keys for the auditor logos this page can show. */
export type AuditLogoKey = "logo/accretion-horizontal" | "logo/zenith";

export interface AuditReport {
    /** Stable, id-safe key: the tab value, the React key, and element ids. */
    key: string;
    /** The auditor's own report identifier, shown as the report's figure. A
     *  draft without one shows the version printed on its cover. */
    id: string;
    title: string;
    auditor: string;
    /** The audit's toggle label when one auditor has several rounds (e.g.
     *  "Accretion 1"). Also names the report for screen readers, so two
     *  rounds never announce the same. Defaults to `auditor`. */
    tabLabel?: string;
    /** Date shown for this report in the wiki's Audits and Security page. */
    date: string;
    /** The published report, or `null` while it is unpublished. An audit with
     *  no link renders no Read Report action, rather than a link to nowhere. */
    href: string | null;
    /** A fixed-name state shown as a Badge, e.g. "Draft". */
    status?: string;
    /** The dossier's surface family: `tint` (Olive, the default) or `alt`
     *  (Fig). The audit's tab and card share it. */
    surface?: "tint" | "alt";
    /** The auditor's design-system logo key. Without one, the card shows the
     *  report ID as its figure instead. */
    logo?: AuditLogoKey;
}

/**
 * Every audit, default tab first. Each gets a panel tab named for its auditor
 * (or its `tabLabel`, for an auditor with more than one round),
 * and shows at hero scale one at a time (ui-guide 3d: one hero figure on
 * screen). Adding an audit is a data change only: append its object, with no
 * component edit. The tuple type guarantees there is always a first audit to
 * open on.
 */
export const AUDITS: readonly [first: AuditReport, ...rest: AuditReport[]] = [
    {
        // One Accretion tab, the August 16, 2026 listing of the A26SOL2
        // report, by product decision (the July 10 listing opens the same
        // PDF).
        key: "accretion-a26sol2",
        logo: "logo/accretion-horizontal",
        id: "A26SOL2",
        title: "USDv Programs & Controls",
        auditor: "Accretion",
        date: "Aug 16, 2026",
        href: "https://github.com/SolomonLabs/solana-program-audits-public/blob/main/2026-accretion-solomon-labs-audit-A26SOL2.pdf"
    },
    {
        // Published at SolomonLabs/solana-program-audits-public (uploaded
        // 2026-09-15). Cover: "Smart Contract Security Assessment", Version 1.1,
        // no report ID and no draft mark. Audit window August 25 to September
        // 10, 2026, report published September 10, 2026; scope is the
        // chancery-audits repository at commit 9eccca8d. Its findings are not
        // summarised here. It supersedes the Version 1.0 draft this entry
        // first listed, which had no link.
        key: "zenith",
        logo: "logo/zenith",
        id: "Version 1.1",
        // Shown under the same heading as Accretion by product decision; the
        // report cover itself reads "Smart Contract Security Assessment".
        title: "USDv Programs & Controls",
        auditor: "Zenith",
        date: "Sep 15, 2026",
        surface: "alt",
        href: "https://github.com/SolomonLabs/solana-program-audits-public/blob/main/2026-09-15_Zenith-Audit-Report-Solomon.pdf"
    }
];

export const AUTHORITY_DISCLOSURE_SNAPSHOT = {
    isoDate: "2026-09-04",
    label: "September 4, 2026"
} as const;

export const ONCHAIN_AUTHORITY_SNAPSHOT = {
    isoDate: "2026-09-15",
    label: "September 15, 2026",
    timeUtc: "22:23 UTC",
    slot: 447_362_593
} as const;

export interface AuthorityConfiguration {
    id: string;
    name: string;
    capability: string;
    approvalsRequired: number;
    votingMembers: number;
    squadsTimelock: string;
    multisigAddress: string;
    multisigHref: string;
    note?: string;
}

function authorityConfiguration(configuration: Omit<AuthorityConfiguration, "multisigHref">): AuthorityConfiguration {
    return {
        ...configuration,
        multisigHref: solscanAccountUrl(configuration.multisigAddress)
    };
}

export const PUBLISHED_AUTHORITIES: readonly AuthorityConfiguration[] = [
    authorityConfiguration({
        id: "upgrade",
        name: "Program Upgrade",
        capability: "Upgrades the USDv Controls Program.",
        approvalsRequired: 3,
        votingMembers: 8,
        squadsTimelock: "2 days",
        multisigAddress: "4hFbN3HSmz4vBVNsv3MP8DfaKJKjAunrfzaXmQ4x1pQP"
    }),
    authorityConfiguration({
        id: "governance",
        name: "Governance",
        capability: "Sets assets, policies, permissions, and economic parameters.",
        approvalsRequired: 3,
        votingMembers: 6,
        squadsTimelock: "N/A",
        multisigAddress: "69EGL7EnwL5gGCGsf1m4Rp5tzi6dm5TmfCL32WgjRWzD"
    }),
    authorityConfiguration({
        id: "operations",
        name: "Operations",
        capability: "Settles within Governance limits.",
        approvalsRequired: 2,
        votingMembers: 7,
        squadsTimelock: "N/A",
        multisigAddress: "5xhpNfxUtFYDn4A4d1FhcCWP8E52JQJf9iwCmip78axG"
    }),
    authorityConfiguration({
        id: "emergency",
        name: "Emergency",
        capability: "Pauses USDv; Governance can unpause it.",
        approvalsRequired: 1,
        votingMembers: 6,
        squadsTimelock: "N/A",
        multisigAddress: "uRd4UGLznc2YW2N6bVvVxwaRCCKwEfuG2VuokRfajNQ"
    }),
    authorityConfiguration({
        id: "treasury",
        name: "Treasury",
        capability: "Holds reserves and cannot sign USDv Controls Program instructions.",
        approvalsRequired: 2,
        votingMembers: 5,
        squadsTimelock: "N/A",
        multisigAddress: "AqSWejgnsyE7N32TKiaVgtqiBMxRQP5vm5bCDkejaePQ"
    }),
    authorityConfiguration({
        id: "enforcement",
        name: "Enforcement",
        capability: "Freezes and thaws accounts where legally required.",
        approvalsRequired: 2,
        votingMembers: 5,
        squadsTimelock: "N/A",
        multisigAddress: "E2RkC3FCXPGrDee2w2NuSmbNZ5TQqxPCrmjPhRJaoHXZ"
    }),
    authorityConfiguration({
        id: "insurance",
        name: "Insurance Admin",
        capability: "Administers the reserved insurance module.",
        approvalsRequired: 2,
        votingMembers: 5,
        squadsTimelock: "N/A",
        multisigAddress: "H8yk1vwT8Mg84X9cMNgUkz2BrVhuVoTPxAzTUGH8LBif",
        note: "Reserved, not enabled"
    })
];

export const ONCHAIN_AUTHORITIES: readonly AuthorityConfiguration[] = [
    authorityConfiguration({
        id: "upgrade",
        name: "Program Upgrade",
        capability: "Upgrades the USDv Controls Program.",
        approvalsRequired: 3,
        votingMembers: 7,
        squadsTimelock: "2 days",
        multisigAddress: "4hFbN3HSmz4vBVNsv3MP8DfaKJKjAunrfzaXmQ4x1pQP"
    }),
    authorityConfiguration({
        id: "governance",
        name: "Governance",
        capability: "Sets assets, policies, permissions, and economic parameters.",
        approvalsRequired: 1,
        votingMembers: 6,
        squadsTimelock: "0 seconds",
        multisigAddress: "69EGL7EnwL5gGCGsf1m4Rp5tzi6dm5TmfCL32WgjRWzD"
    }),
    authorityConfiguration({
        id: "operations",
        name: "Operations",
        capability: "Settles within Governance limits.",
        approvalsRequired: 1,
        votingMembers: 7,
        squadsTimelock: "0 seconds",
        multisigAddress: "5xhpNfxUtFYDn4A4d1FhcCWP8E52JQJf9iwCmip78axG"
    }),
    authorityConfiguration({
        id: "emergency",
        name: "Emergency",
        capability: "Pauses USDv; Governance can unpause it.",
        approvalsRequired: 1,
        votingMembers: 6,
        squadsTimelock: "0 seconds",
        multisigAddress: "uRd4UGLznc2YW2N6bVvVxwaRCCKwEfuG2VuokRfajNQ"
    }),
    authorityConfiguration({
        id: "treasury",
        name: "Treasury",
        capability: "Holds reserves and cannot sign USDv Controls Program instructions.",
        approvalsRequired: 1,
        votingMembers: 7,
        squadsTimelock: "0 seconds",
        multisigAddress: "AqSWejgnsyE7N32TKiaVgtqiBMxRQP5vm5bCDkejaePQ"
    }),
    authorityConfiguration({
        id: "enforcement",
        name: "Enforcement",
        capability: "Freezes and thaws accounts where legally required.",
        approvalsRequired: 1,
        votingMembers: 6,
        squadsTimelock: "0 seconds",
        multisigAddress: "E2RkC3FCXPGrDee2w2NuSmbNZ5TQqxPCrmjPhRJaoHXZ"
    }),
    authorityConfiguration({
        id: "insurance",
        name: "Insurance Admin",
        capability: "Administers the reserved insurance module.",
        approvalsRequired: 1,
        votingMembers: 6,
        squadsTimelock: "0 seconds",
        multisigAddress: "H8yk1vwT8Mg84X9cMNgUkz2BrVhuVoTPxAzTUGH8LBif",
        note: "Reserved, not enabled"
    })
];

export interface ProgramTimelock {
    riskClass: 0 | 1 | 2 | 3 | 4 | 5;
    name: string;
    delay: string;
}

export const PROGRAM_TIMELOCKS: readonly ProgramTimelock[] = [
    {
        riskClass: 0,
        name: "Restrictive Immediate",
        delay: "0 seconds"
    },
    {
        riskClass: 1,
        name: "Routine Operations",
        delay: "0 seconds"
    },
    {
        riskClass: 2,
        name: "Widening",
        delay: "2 hours"
    },
    {
        riskClass: 3,
        name: "High Impact",
        delay: "8 hours"
    },
    {
        riskClass: 4,
        name: "Dangerous",
        delay: "1 day"
    },
    {
        riskClass: 5,
        name: "Irreversible",
        delay: "2 days"
    }
];

export const AUTHORITY_TRANSFER_MINIMUM_SLOTS = 12_000;
